import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import TrainOutlined from '@mui/icons-material/TrainOutlined';
import LocationCityOutlined from '@mui/icons-material/LocationCityOutlined';
import type { DriveTime } from '../../../types/Supplier';

const ICONS: Record<DriveTime['type'], typeof DirectionsBoatOutlined> = {
  PORT: DirectionsBoatOutlined,
  RAIL_RAMP: TrainOutlined,
  DOWNTOWN: LocationCityOutlined,
};

interface LocationProfileNearbyProps {
  /** From the profile API, already ordered PORT, RAIL_RAMP, DOWNTOWN; absent or empty hides the section. */
  driveTimes?: DriveTime[];
  /** Places with a point become buttons that show them on the map. */
  onShowOnMap?: (driveTime: DriveTime) => void;
}

const ITEM_SX = { display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 } as const;

const LocationProfileNearby = ({ driveTimes, onShowOnMap }: LocationProfileNearbyProps) => {
  if (!driveTimes?.length) {
    return null;
  }
  return (
    <Box sx={{ mt: '1.5rem' }}>
      <Typography className="ss-profile-desc-title">Nearby</Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: '#5C6771', mb: 1.5 }}>Approximate drive times</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        {driveTimes.map((driveTime) => {
          const { type, name, miles, minutes } = driveTime;
          const Icon = ICONS[type] ?? LocationCityOutlined;
          const content = <>
              <Box sx={{
                width: 40, height: 40, flexShrink: 0, borderRadius: '8px', bgcolor: '#F5F5F5',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#324559',
              }}>
                <Icon fontSize="small" aria-hidden="true" />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4, color: '#324559' }}>{name}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.4, color: '#5C6771' }}>{miles} mi · {minutes} min</Typography>
              </Box>
          </>;
          if (!onShowOnMap || driveTime.lat == null || driveTime.lng == null) {
            return <Box key={type} sx={ITEM_SX}>{content}</Box>;
          }
          return (
            // The label replaces the visible text for screen readers, so it carries the drive time too.
            <ButtonBase key={type} onClick={() => onShowOnMap(driveTime)}
              aria-label={`${name}, ${miles} mi, ${minutes} min drive. Show on the map`} sx={{
              ...ITEM_SX, justifyContent: 'flex-start', textAlign: 'left', borderRadius: '8px', p: 0.5, m: -0.5,
              '&:hover, &.Mui-focusVisible': { bgcolor: '#F5F5F5' },
            }}>
              {content}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default LocationProfileNearby;
